"use client";

/**
 * What to bring, grouped by how much it matters: each piece's title,
 * description, the weather it is for when it depends on the forecast, and a
 * link to more about it. What the trails ask for is in the list like the rest,
 * without saying which trail: that is for the leader's field sheet.
 */
import { CloudSunRain, ExternalLink } from "lucide-react";
import type { EquipmentItem, Weather } from "@/lib/types/event";
import { WEATHER_WORDS, listWords } from "@/components/event/labels";
import { safeHref } from "@/components/event/maps";

/**
 * When a piece of equipment that depends on the forecast is for: "Se a
 * previsão for de chuva ou neve". Nothing for a piece that is always for.
 */
export const equipmentWhen = (weather: Weather[] | string[] | null | undefined) => {
  if (!weather || !weather.length) {
    return "";
  }
  const words = weather.map((w) => WEATHER_WORDS[w as Weather] || String(w).toLowerCase());
  return `Se a previsão for de ${listWords(words)}`;
};

const LEVELS: [string, string][] = [
  ["REQUIRED", "Obrigatório"],
  ["RECOMMENDED", "Recomendado"],
  ["OPTIONAL", "Opcional"],
];

const LEVEL_TONES: Record<string, string> = {
  Obrigatório: "bg-brand-soft text-brand-dark",
  Recomendado: "bg-amber-soft text-[#7a5a2c]",
  Opcional: "bg-ground text-ink-soft",
};

export function EquipmentList({ items, className = "" }: { items: EquipmentItem[]; className?: string }) {
  if (items.length === 0) {
    return <p className={`text-[14px] text-ink-soft ${className}`}>Não há equipamento indicado para este evento.</p>;
  }
  const knownLevels = LEVELS.map(([level]) => level);
  const groups: [string, EquipmentItem[]][] = [
    ...LEVELS.map(([level, label]): [string, EquipmentItem[]] => [label, items.filter((item) => item.required === level)]),
    ["Outro equipamento", items.filter((item) => !knownLevels.includes(item.required || ""))],
  ];

  return (
    <div className={`flex flex-col gap-5 ${className}`}>
      {groups
        .filter(([, group]) => group.length > 0)
        .map(([label, group]) => (
          <section key={label}>
            <h4 className={`mb-2 inline-block rounded-full px-2.5 py-0.5 text-[12px] font-bold uppercase tracking-wide ${LEVEL_TONES[label] || LEVEL_TONES.Opcional}`}>{label}</h4>
            <ul className="divide-y divide-line">
              {group.map((item) => {
                const website = safeHref(item.website);
                const when = equipmentWhen(item.weather);
                return (
                  <li key={item.id} className="py-2.5 first:pt-0 last:pb-0">
                    <div className="text-[14.5px] font-semibold">{item.title}</div>
                    {item.description && <div className="mt-0.5 whitespace-pre-line text-[13.5px] text-ink-soft">{item.description}</div>}
                    {when && (
                      <div className="mt-1 inline-flex items-center gap-1.5 text-[12.5px] text-secondary">
                        <CloudSunRain size={14} /> {when}
                      </div>
                    )}
                    {website && (
                      <a href={website} target="_blank" rel="noopener noreferrer" className="mt-1 ml-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand">
                        Saber mais <ExternalLink size={12} />
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
    </div>
  );
}

/** the section's content: the list once loaded, nothing until then (the page draws the heading) */
export function EventEquipment({ items }: { items: EquipmentItem[] | null | undefined }) {
  if (!items) {
    return null;
  }
  return <EquipmentList items={items} />;
}
